// The change queue's way to the server (BL-2345, ADR 066 — Offline Mode § 4): each op replays
// through the online door the app already uses, via the typed client, and a staged file is
// uploaded through the same signed-URL flow as an online photo. Upload URLs are minted here, at
// replay, never stored in the queue (§ 4.3).
import type { ChangeQueueCall, ChangeQueueOp } from '@auctogon/admin-isomorphic/isomorphic/offline/change-queue-entry';
import type { LotStateRequest } from '@auctogon/admin-isomorphic/isomorphic/offline/lot-desired-state';
import type { api as typedApi } from '../../api-client/typed-client';
import { putViaSignedUrl, type PutViaSignedUrlResult } from '../../api-client/upload-client';
import type { StagedFile } from '../../gateways/native-device';

/** One replayed request. */
export interface ReplaySendInput {
  op: ChangeQueueOp;
  call: ChangeQueueCall;
  signal: AbortSignal;
}

/** One staged file to upload before its add replays: a video's file or a document. */
export interface ReplayUploadInput {
  /** `media` goes under the lot's inventory item, `document` under the lot. */
  op: 'media' | 'document';
  /** The lot's server id. */
  lotId: string;
  stagedFileId: string;
  file: StagedFile;
  blob: Blob;
}

/**
 * Where the upload landed, the session having expired, or the reason it did not land.
 * `unauthorized` is a 401 reading the lot or minting the upload URL: the user signs in again and
 * the entry replays (ADR 066 — Offline Mode § 4.6, BUG-2070). `retryLater` marks a server error,
 * or storage refusing the signed URL (it expires): the entry stays queued and the next pass mints
 * a fresh URL. Anything else is the server refusing the file.
 */
export type ReplayUploadResult =
  | { ok: true; storagePath: string }
  | { ok: false; unauthorized: true }
  | { ok: false; unauthorized?: false; message: string; retryLater?: boolean };

/** One lot-state request (BL-2681): a lot's final fields as one `PUT /api/lots/:id/state`. */
export interface ReplayLotStateInput {
  lotId: string;
  body: LotStateRequest;
  signal: AbortSignal;
}

/** The `meta` part of one media ingest request (the server's `LotMediaIngestMetaSchema`). */
export interface ReplayMediaMeta {
  position: number;
  isCover: boolean;
  type: 'photo' | 'video';
  contentType?: string;
  /** A video's, or a photo's, file already in storage. */
  storagePath?: string;
  caption?: string;
}

/** One media ingest (BL-2815): a photo with its thumbnail, a video attach, or a file already in storage attached by path (BL-2816). */
export interface ReplayMediaInput {
  lotId: string;
  /** The client media id, which is also the op id. */
  mediaId: string;
  photo?: { blob: Blob; name: string };
  /** Absent for a photo or video attached by `meta.storagePath` (BL-2816): its thumbnail is already in storage. */
  thumbnail?: Blob;
  meta: ReplayMediaMeta;
  signal: AbortSignal;
}

/** The server, as the replay sees it. */
export interface ReplayTransport {
  /**
   * @param input - the lot, the media id, the files (none for a file attached by its path) and the placement.
   * @returns the HTTP status and parsed JSON body (null when there was none).
   * @throws on a network failure.
   * @sideEffect Sends one multipart ingest write to the server.
   */
  sendMedia(input: ReplayMediaInput): Promise<{ status: number; body: unknown }>;
  /**
   * @param input - the lot and its state request.
   * @returns the HTTP status and parsed JSON body (null when there was none).
   * @throws on a network failure, or {@link MalformedChangeError} when the request names another house.
   * @sideEffect Sends one lot-state write to the server.
   */
  sendLotState(input: ReplayLotStateInput): Promise<{ status: number; body: unknown }>;
  /**
   * Read a lot as the server holds it now, to show a conflict's other side.
   *
   * @param input - the lot's id and an abort signal.
   * @returns the HTTP status and parsed JSON body (null when there was none).
   * @throws on a network failure.
   */
  readLot(input: { lotId: string; signal: AbortSignal }): Promise<{ status: number; body: unknown }>;
  /**
   * @param input - the op, its call and an abort signal.
   * @returns the HTTP status and parsed JSON body (null when there was none).
   * @throws on a network failure, or {@link MalformedChangeError} when the call lacks a path param.
   * @sideEffect Sends one write to the server.
   */
  send(input: ReplaySendInput): Promise<{ status: number; body: unknown }>;
  /**
   * @param input - the staged file and the lot it belongs to.
   * @returns the storage path, or the refusal.
   * @throws on a network failure.
   * @sideEffect Uploads the file to object storage.
   */
  upload(input: ReplayUploadInput): Promise<ReplayUploadResult>;
}

/** Thrown out of a pass when the server cannot be reached: the engine logs it and still downloads. */
export class ReplayTransportError extends Error {}

/** A stored call that cannot address its door: an entry an older build wrote differently. */
export class MalformedChangeError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'MalformedChangeError';
  }
}

const NO_ITEM = 'This lot has no inventory item to hold its photos.';

/**
 * Lot photos live in the lot's inventory item's storage slot (the `inventory` upload entity is
 * keyed by inventory item, as the online gallery uploads). The item never changes for a lot, so
 * one read per lot per transport.
 */
function createInventoryItemLookup(api: typeof typedApi) {
  const known = new Map<string, string>();
  return async (lotId: string): Promise<{ ok: true; itemId: string } | Exclude<ReplayUploadResult, { ok: true }>> => {
    const hit = known.get(lotId);
    if (hit) return { ok: true, itemId: hit };
    // Rejects on a network failure, which the replay treats as a lost connection.
    const { status, body } = await api.lots.id.index.get.withStatus({ params: { id: lotId } });
    if (!body.success) {
      if (status === 401) return { ok: false, unauthorized: true };
      const message = body.error || 'The server could not read this lot.';
      return status >= 500 ? { ok: false, message, retryLater: true } : { ok: false, message };
    }
    const itemId = body.data.inventoryItemId;
    if (!itemId) return { ok: false, message: NO_ITEM };
    known.set(lotId, itemId);
    return { ok: true, itemId };
  };
}

/**
 * A queued call whose body names a house refuses to replay under another one: the store is scoped
 * to one tenant, so a mismatch means a corrupted entry, and the server must never see it.
 */
function assertSameHouse(call: ChangeQueueCall, tenantId: string): void {
  const body = call.body;
  if (typeof body !== 'object' || body === null || Array.isArray(body) || !('tenantId' in body)) return;
  if (body.tenantId !== tenantId) throw new MalformedChangeError('This change belongs to another house.');
}

function param(call: ChangeQueueCall, key: string): string {
  const value = call.params[key];
  if (!value) throw new MalformedChangeError('This change is missing the record it belongs to.');
  return value;
}

async function readBody(res: Response): Promise<unknown> {
  const text = await res.text();
  if (text.length === 0) return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    // A load balancer's HTML error page: the status alone classifies it.
    return null;
  }
}

function rawCall(api: typeof typedApi, { op, call, signal }: ReplaySendInput): Promise<Response> {
  const init = { signal };
  const body = call.body;
  switch (op) {
    case 'lot.relist':
      return api.lots.placeInAuction.post.raw({ body, init });
    case 'lot.move':
      return api.lots.id.index.patch.raw({ params: { id: param(call, 'id') }, body, init });
    case 'lot.status':
      return api.lots.batch.post.raw({ body, init });
    case 'lot.document.add':
      return api.lots.id.documents.post.raw({ params: { id: param(call, 'id') }, body, init });
    case 'lot.document.remove':
      return api.lots.id.document.delete.raw({ params: { id: param(call, 'id'), documentId: param(call, 'documentId') }, init });
    case 'note.create':
      return api.internal_notes.index.post.raw({ body, init });
    case 'note.update':
      return api.internal_notes.detail.patch.raw({ params: { noteId: param(call, 'noteId') }, body, init });
    case 'note.delete':
      return api.internal_notes.detail.delete.raw({ params: { noteId: param(call, 'noteId') }, init });
  }
}

/**
 * The statuses storage answers a signed URL it no longer honours with: GCS says 400 `ExpiredToken`,
 * S3-style stores 403, some 401. A URL lives 2 hours (`UPLOAD_URL_TTL_SECONDS`), so a large video
 * suspended mid-upload meets one.
 */
const EXPIRED_URL_STATUSES = new Set([400, 401, 403]);

/**
 * Read a failed presign-and-PUT. Our server's 401 is a signed-out session. Storage refusing the
 * signed URL means it expired while the app was suspended, so a later pass mints a fresh one (the
 * MAX_REPLAY_ATTEMPTS cap still ends a PUT storage never takes). A server error (after the upload
 * client's own retry) waits too. Any other refusal is final: the file fails, or a poster is dropped.
 */
function uploadRefusal(put: Extract<PutViaSignedUrlResult, { success: false }>): Exclude<ReplayUploadResult, { ok: true }> {
  if (put.step === 'presign' && put.status === 401) return { ok: false, unauthorized: true };
  const expiredUrl = put.step === 'put' && put.status != null && EXPIRED_URL_STATUSES.has(put.status);
  const retryLater = expiredUrl || (put.status != null && put.status >= 500);
  return retryLater ? { ok: false, message: put.error, retryLater: true } : { ok: false, message: put.error };
}

/**
 * The live transport: the typed client for the doors, the signed-URL flow for files.
 *
 * @param input.api - the typed API client (it must be online: the replay runs only then).
 * @param input.tenantId - the signed-in tenant, which the upload's presign names.
 * @returns the transport.
 */
export function createReplayTransport({ api, tenantId }: { api: typeof typedApi; tenantId: string }): ReplayTransport {
  const inventoryItemOf = createInventoryItemLookup(api);
  return {
    sendLotState: async ({ lotId, body, signal }) => {
      if (body.tenantId !== tenantId) throw new MalformedChangeError('This change belongs to another house.');
      const res = await api.lots.id.state.put.raw({ params: { id: lotId }, body, init: { signal } });
      return { status: res.status, body: await readBody(res) };
    },
    readLot: async ({ lotId, signal }) => {
      const res = await api.lots.id.index.get.raw({ params: { id: lotId }, init: { signal } });
      return { status: res.status, body: await readBody(res) };
    },
    sendMedia: async ({ lotId, mediaId, photo, thumbnail, meta, signal }) => {
      const form = new FormData();
      if (photo) form.append('photo', photo.blob, photo.name);
      if (thumbnail) form.append('thumbnail', thumbnail, 'thumbnail.jpg');
      form.append('meta', JSON.stringify(meta));
      const res = await api.lots.id.media_item.put.raw({ params: { id: lotId, mediaId }, body: form, init: { signal } });
      return { status: res.status, body: await readBody(res) };
    },
    send: async (input) => {
      assertSameHouse(input.call, tenantId);
      const res = await rawCall(api, input);
      return { status: res.status, body: await readBody(res) };
    },
    upload: async ({ op, lotId, file, blob }) => {
      // The same storage slots the online form uploads into: lot photos under the lot's inventory
      // item, lot documents under the lot.
      let entityId = lotId;
      if (op === 'media') {
        const item = await inventoryItemOf(lotId);
        if (!item.ok) return item;
        entityId = item.itemId;
      }
      const put = await putViaSignedUrl({
        entityType: op === 'media' ? 'inventory' : 'auction_lot',
        entityId,
        tenantId,
        file: blob,
        fileName: file.name,
        mimeType: file.mimeType,
      });
      if (put.success) return { ok: true, storagePath: put.storagePath };
      return uploadRefusal(put);
    },
  };
}
